import { UserRole } from '../types/user';
/**
 * Email mappings for real database users
 */
export const DEV_USER_EMAILS = {
    // Regular users
    bubba: 'bubba@sparxx.com',
    clown: 'clownie@bertco.com',
    anen: 'anen@bertco.com',
    nasty: 'nasty@review.com',
    // Venue admins
    dom: 'dom@carneros.com',
    bill: 'billy@whetstone.com',
    // Site admins
    siteadmin: 'siteadmin@winex.com',
    mcadmin: 'mcadmin@winex.com',
    // Super admin
    mitch: 'makahn99@gmail.com',
};
/**
 * Predefined development users for easy testing
 * @deprecated Use DEV_USER_EMAILS and database lookup instead
 * Kept for backward compatibility during transition
 * IDs are valid MongoDB ObjectIds so they work with database operations
 */
export const DEV_USERS = {
    anonymous: {
        id: '000000000000000000000000', // Valid ObjectId for anonymous
        email: 'anonymous@dev.local',
        name: 'Anonymous User',
        role: UserRole.ANONYMOUS,
    },
    user: {
        id: '000000000000000000000001', // Valid ObjectId for test user
        email: 'user@dev.local',
        name: 'Test User',
        role: UserRole.USER,
    },
    venue_admin: {
        id: '000000000000000000000002', // Valid ObjectId for venue admin
        email: 'venue.admin@dev.local',
        name: 'Venue Admin',
        role: UserRole.VENUE_ADMIN,
    },
    site_admin: {
        id: '000000000000000000000003', // Valid ObjectId for site admin
        email: 'site.admin@dev.local',
        name: 'Site Admin',
        role: UserRole.SITE_ADMIN,
    },
    super_admin: {
        id: '000000000000000000000004', // Valid ObjectId for super admin
        email: 'super.admin@dev.local',
        name: 'Super Admin',
        role: UserRole.SUPER_ADMIN,
    },
};
/**
 * Check if development authentication bypass is enabled
 */
export const isDevAuthEnabled = () => {
    return process.env.NODE_ENV === 'development' &&
        process.env.ENABLE_DEV_AUTH === 'true';
};
/**
 * Get development user from header value
 * Now queries the database for real user accounts
 *
 * @param headerValue - The x-dev-user header value (email address)
 * @param UserModel - The Mongoose User model for database queries
 * @returns SessionUser if found, null otherwise
 */
export const getDevUserFromHeader = async (headerValue, UserModel) => {
    if (!isDevAuthEnabled() || !headerValue) {
        return null;
    }
    // Check if it's a predefined user email
    const userEmail = Object.values(DEV_USER_EMAILS).includes(headerValue)
        ? headerValue
        : null;
    if (userEmail) {
        // Query database for user
        try {
            const user = await UserModel.findOne({ email: userEmail }).lean();
            if (user && user.deactivated) {
                console.warn(`⚠️ Dev auth: user ${userEmail} is deactivated, treating as anonymous`);
                return null;
            }
            if (user) {
                return {
                    id: user._id.toString(),
                    email: user.email,
                    name: user.name || user.userName,
                    role: user.role,
                    image: user.image,
                    avatar: user.avatar,
                    onboardingComplete: user.onboardingComplete ?? false,
                    emailVerified: user.emailVerified,
                };
            }
            else {
                console.warn(`⚠️ Dev user not found in database: ${userEmail}`);
                return null;
            }
        }
        catch (error) {
            console.error(`❌ Error querying dev user from database:`, error);
            return null;
        }
    }
    // Fallback: Check if it's a predefined user key (backward compatibility)
    if (headerValue in DEV_USERS) {
        const devUser = DEV_USERS[headerValue];
        console.warn(`⚠️ Using deprecated DEV_USERS constant for user key: ${headerValue}`);
        return {
            id: devUser.id,
            email: devUser.email,
            name: devUser.name,
            role: devUser.role,
            image: devUser.image,
            onboardingComplete: false,
        };
    }
    // Check if it's a JSON string for custom user (backward compatibility)
    try {
        const customUser = JSON.parse(headerValue);
        if (customUser.id && customUser.email && customUser.role) {
            console.warn(`⚠️ Using deprecated JSON custom user format`);
            return {
                id: customUser.id,
                email: customUser.email,
                name: customUser.name || 'Custom Dev User',
                role: customUser.role,
                image: customUser.image,
                onboardingComplete: false,
            };
        }
    }
    catch {
        // Not valid JSON, ignore
    }
    return null;
};
/**
 * Development authentication headers
 */
export const DEV_AUTH_HEADERS = {
    USER: 'x-dev-user',
};
/**
 * Helper to create dev auth headers for testing
 */
export const createDevAuthHeaders = (userKey) => {
    if (!isDevAuthEnabled()) {
        throw new Error('Development auth is not enabled');
    }
    return {
        [DEV_AUTH_HEADERS.USER]: userKey,
    };
};
